import { useEffect, useRef } from "react"
import './AsciiTerrain.css'

export function AsciiTerrain() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const container = canvas.parentElement as HTMLElement | null
    if (!container) return

    const isMobile = window.matchMedia("(max-width: 768px)").matches
    const pixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.25 : 2)

    let logicalWidth = 0
    let logicalHeight = 0

    const resize = () => {
      const rect = container.getBoundingClientRect()
      const nextWidth = Math.max(1, Math.floor(rect.width))
      const nextHeight = Math.max(1, Math.floor(rect.height))

      if (nextWidth === logicalWidth && nextHeight === logicalHeight) return

      logicalWidth = nextWidth
      logicalHeight = nextHeight

      canvas.width = Math.floor(logicalWidth * pixelRatio)
      canvas.height = Math.floor(logicalHeight * pixelRatio)
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    }
    resize()

    const chars = ["+", "&", "@", "\\", "|", "<", ">", "Ø"]
    const colors = ["#0066ff", "#00ff88", "#ffff00", "#00ccff"]

    let mouseX = -9999
    let mouseY = -9999
    let time = 0
    let interactionTimeout: ReturnType<typeof setTimeout> | null = null

    // Mouse move on canvas only (not window) — prevents scroll-triggered glitches
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      mouseX = e.clientX - rect.left
      mouseY = e.clientY - rect.top
    }

    const handleMouseLeave = () => {
      mouseX = -9999
      mouseY = -9999
    }

    // Touch: react on tap, reset after timeout
    const handleTouch = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const rect = canvas.getBoundingClientRect()
        mouseX = e.touches[0].clientX - rect.left
        mouseY = e.touches[0].clientY - rect.top
      }
      if (interactionTimeout) clearTimeout(interactionTimeout)
      interactionTimeout = setTimeout(() => {
        mouseX = -9999
        mouseY = -9999
      }, 2000)
    }

    canvas.addEventListener("mousemove", handleMouseMove)
    canvas.addEventListener("mouseleave", handleMouseLeave)
    canvas.addEventListener("touchstart", handleTouch, { passive: true })
    window.addEventListener("resize", resize, { passive: true })

    let animationFrameId: number | null = null
    let lastFrameTime = performance.now()
    let shouldAnimate = true
    let isInViewport = true
    const targetFPS = isMobile ? 20 : 30
    const frameInterval = 1000 / targetFPS
    const interactionRadius = isMobile ? 220 : 300
    const interactionRadiusSq = interactionRadius * interactionRadius

    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        isInViewport = entry.isIntersecting
        shouldAnimate = isInViewport && document.visibilityState === "visible"
      },
      { threshold: 0.1 }
    )
    visibilityObserver.observe(container)

    const onVisibilityChange = () => {
      shouldAnimate = isInViewport && document.visibilityState === "visible"
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    const animate = (currentTime: number) => {
      const elapsed = currentTime - lastFrameTime

      if (!shouldAnimate) {
        lastFrameTime = currentTime
        animationFrameId = requestAnimationFrame(animate)
        return
      }
      
      if (elapsed >= frameInterval) {
        ctx.fillStyle = "#000"
        ctx.fillRect(0, 0, logicalWidth, logicalHeight)

        const fontSize = isMobile
          ? Math.max(12, Math.min(18, logicalWidth / 44))
          : Math.max(12, Math.min(20, logicalWidth / 80))
        const xStep = fontSize * (isMobile ? 0.72 : 0.6)
        const yStep = isMobile ? fontSize * 1.06 : fontSize
        ctx.font = `${fontSize}px monospace`

        const cols = Math.floor(logicalWidth / xStep)
        const rows = Math.floor(logicalHeight / yStep)

        time += 0.02

        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            const px = x * xStep
            const py = y * yStep

            const dx = px - mouseX
            const dy = py - mouseY
            const distSq = dx * dx + dy * dy

            const wave = Math.sin(x * 0.1 + time) * Math.cos(y * 0.1 + time)
            const influence = Math.max(0, 1 - distSq / interactionRadiusSq)

            const heightValue = wave + influence * 2

            let char = " "
            let color = colors[0]

            if (heightValue > 0.8) {
              char = chars[Math.floor(Math.random() * 2)]
              color = colors[3] // Cyan instead of white
            } else if (heightValue > 0.5) {
              char = chars[2 + Math.floor(Math.random() * 2)]
              color = colors[1] // Green
            } else if (heightValue > 0.2) {
              char = chars[4 + Math.floor(Math.random() * 2)]
              color = colors[0] // Blue
            } else if (heightValue > -0.2) {
              char = chars[Math.floor(Math.random() * chars.length)]
              color = colors[2] // Yellow
            }

            if (char !== " ") {
              ctx.fillStyle = color
              ctx.fillText(char, px, py)
            }
          }
        }

        lastFrameTime = currentTime
      }

      animationFrameId = requestAnimationFrame(animate)
    }
    animationFrameId = requestAnimationFrame(animate)

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
      if (interactionTimeout) clearTimeout(interactionTimeout)
      visibilityObserver.disconnect()
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("resize", resize)
      canvas.removeEventListener("mousemove", handleMouseMove)
      canvas.removeEventListener("mouseleave", handleMouseLeave)
      canvas.removeEventListener("touchstart", handleTouch)
    }
  }, [])

  return (
    <section className="ascii-terrain-container">
      <canvas ref={canvasRef} className="ascii-terrain-canvas" />
    </section>
  )
}
